import { EventDetailsColumnType } from '@frontend/sports/betting-offer/feature/model';
import { EventFactory, EventOption, GridSourceEvent } from '@frontend/sports/grid/core/feature/factories';
import { GridGrouping, GridMedia, GridModel } from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridState, gridStateSelector } from '@frontend/sports/grid/core/feature/store';
import { MediaState, mediaStateSelector } from '@frontend/sports/grid/media/feature/state';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable, ReplaySubject, Subscription, combineLatest, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs';

export class ObservableGrid extends Observable<GridModel> {
    id: string;
    private initialized = false;

    private subject = new ReplaySubject<GridModel>(1);
    private subscription: Subscription;

    constructor(
        private grid: GridModel,
        private store: Store,
        private factory: EventFactory,
        source: Observable<GridModel>,
        private gridFactoryTeardown: () => void,
    ) {
        super();

        this.id = grid.id;

        this.source = this.subject.asObservable();

        this.subscription = source
            .pipe(
                tap((current) => {
                    this.init(current);
                }),
                switchMap((initial) =>
                    combineLatest([
                        store.select(gridStateSelector).pipe(
                            //Start with the initial config emitted via the source to avoid waiting for the next "store" emission which is dispatched via the tap 3 lines above
                            //shouldn't really be needed in the first place, but it's a hack for the tests to work, and will avoid a redundant task schedule
                            startWith({ [this.id]: initial }),
                        ),
                        //usually it's not advised to combine selectors in this way, made an exception here to avoid coupling the two states together
                        store.select(mediaStateSelector),
                    ]).pipe(
                        map(([gridState, mediaState]) => this.getModel(gridState, mediaState)),
                        distinctUntilChanged((current, previous) => this.compareModel(current, previous)),
                        filter<GridModel | undefined, GridModel>((value) => value !== undefined),
                    ),
                ),
                tap((current) => {
                    this.grid = current;
                    this.subject.next(current);
                }),
            )
            .subscribe();
    }

    getState(): GridModel {
        return this.grid;
    }

    destroy(): void {
        this.subject.complete();
        this.subscription?.unsubscribe();
        this.store.dispatch(GridActions.destroy({ gridId: this.id }));
        this.gridFactoryTeardown();
    }

    addEvents(events: GridSourceEvent | GridSourceEvent[], options?: EventOption, grouping?: GridGrouping): void {
        const eventsList = Array.isArray(events) ? events : [events];

        this.factory.create(eventsList, options).subscribe((eventsMapped) => {
            this.store.dispatch(GridActions.addEvents({ payload: { id: this.id, events: eventsMapped, grouping } }));
        });
    }

    removeEvents(eventIds: string[]): void {
        this.store.dispatch(GridActions.removeEvents({ payload: { id: this.id, eventIds } }));
    }

    private init(grid: GridModel): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;
        this.store.dispatch(GridActions.init({ payload: grid }));
    }

    private getModel = (gridState: IGridState, mediaState: MediaState) => {
        const grid = gridState[this.id];
        if (!grid) {
            return;
        }

        if (!mediaState) {
            return grid;
        }

        const mediaTab = mediaState.activeTab;
        const media: GridMedia = {
            enabled: mediaState.active,
            videoEvent: (mediaTab === EventDetailsColumnType.Video && mediaState.video.eventId) || '',
            animationEvent: (mediaTab === EventDetailsColumnType.Animation && mediaState.animation.eventId) || '',
            statsEvent: (mediaTab === EventDetailsColumnType.Stats && mediaState.statistics.eventId) || '',
        };

        if (isEqual(this.grid.media, media)) {
            return { ...grid, media: this.grid.media };
        }

        return { ...grid, media };
    };

    private compareModel = (current: GridModel | undefined, previous: GridModel | undefined) => {
        if (current === undefined && previous === undefined) {
            return true;
        }
        if (current === undefined || previous === undefined) {
            return false;
        }
        const currentKeys = Object.keys(current) as (keyof GridModel)[];
        const previousKeys = Object.keys(previous) as (keyof GridModel)[];

        if (currentKeys.length !== previousKeys.length) {
            return false;
        }

        for (const key of previousKeys) {
            if (current[key] !== previous[key]) {
                return false;
            }
        }

        return true;
    };
}
